// useAppCoherence (VW-MIG-V2.04 / CFR-VW-145) — the reusable coherence-consumer primitive.
//
// Reads an app instance's STALE subscription worklist from GET /subscriptions/stale (the seam over
// the built subscription service). When a subscribed asset (a BoM/submodel the app ran on) is
// recalled/superseded, the app's binding flips to `stale` and this surfaces it — the redesign's
// "on BoM v3 · stale" consumer signal. Wall-filtered server-side (INV-2); own-instance worklist.
//
// SCOPE: this reports the stale STATE (real, from the seam). The "rerun/recompute" action is
// app-specific (each consumer recomputes differently — no generic recompute endpoint), so a rerun
// affordance is the consuming app's own wiring, not this primitive.
import { getJson, useScopedFetch } from "../pages/scoped/useScopedFetch";

export interface StaleSubscription {
  id: string;
  asset_kind: string;
  asset_id: string;
  status: string;
  /** The version this app was pinned to (null = unpinned binding). */
  pinned_version?: number | null;
  /** The asset's info classification, for the drill-in label. */
  info_class?: string;
}

export interface AppCoherence {
  /** Count of stale subscriptions for this app instance (0 = fresh / current). */
  staleCount: number;
  /** The stale worklist itself — which subscribed assets changed (drill-in / fan-out legibility). */
  stale: StaleSubscription[];
  /** True once the stale query has resolved (distinguishes "fresh" from "still loading"). */
  ready: boolean;
}

export function useAppCoherence(
  appInstanceId: string | undefined,
  readerScopeNodeId: string | undefined,
  opts?: { apiBase?: string; fetchImpl?: typeof fetch },
): AppCoherence {
  const apiBase = opts?.apiBase ?? "/api/workspace";
  const fetchImpl = opts?.fetchImpl;
  const state = useScopedFetch<StaleSubscription[]>(async () => {
    if (!appInstanceId || !readerScopeNodeId) return [];
    return getJson<StaleSubscription[]>(
      apiBase,
      `/subscriptions/stale?app_instance_id=${encodeURIComponent(appInstanceId)}` +
        `&reader_scope_node_id=${encodeURIComponent(readerScopeNodeId)}`,
      fetchImpl,
    );
  }, [apiBase, fetchImpl, appInstanceId, readerScopeNodeId]);

  const stale = state.kind === "ready" && Array.isArray(state.data) ? state.data : [];
  return { staleCount: stale.length, stale, ready: state.kind === "ready" };
}
